import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { connect } from 'react-redux';

import { titleize } from 'helpers/Formatter';

import { Subcontent, AntdButton as Button } from '@yuhuinc/ui-library/';

import { RentableItem } from './RentableItem';
import { RentableItemForm } from './RentableItemForm';
import { getTranslatedString } from 'helpers/i18n';
import { media, sizes } from 'helpers/MediaHelper';

import {
  storeCurrentRentableItemsForLease,
  storeConditionalAddendumsForLease,
} from '../store/actions';

const StyledSubcontentContainer = styled.div`
  margin-bottom: 16px;
`;

const StyledButton = styled(Button)`
  ${media.mobile`
    margin-bottom: 14px;
  `};
`;

const RentableItemList = ({
  property,
  selectedLeaseStartDate,
  selectedEarlyMoveInDate,
  selectedLeaseEndDate,
  leaseDraftRentableItems,
  storeCurrentRentableItemsForLease,
  currentRentableItemsForLease,
  storeConditionalAddendumsForLease,
  conditionalAddendums,
}) => {
  const [toggleNewItem, setToggleNewItem] = useState(false);
  const [itemForEdit, setItemForEdit] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [editIndex, setEditIndex] = useState();

  const setValidRentableItemsInStore = (rentableItems) => {
    storeCurrentRentableItemsForLease(rentableItems);
    if (!isEmpty(conditionalAddendums)) {
      storeConditionalAddendumsForLease(rentableItems, conditionalAddendums);
    }
  };

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;

  const getRentableAssets = () => {
    if (isEmpty(property)) {
      return [];
    }

    // returns array of uniq rentable ancillary items to choose from.
    // filters only rentable items and maps item types and Set reduces to only unique ones
    // then map to label and value to be used in ui library select field
    return [
      ...new Set(
        property.ancillary_items
          .filter((ancillaryItem) => ancillaryItem.is_rentable && ancillaryItem.is_asset)
          .map((ancillaryItem) => ancillaryItem.item_type)
      ),
    ].map((type) => ({
      label: type,
      value: type,
    }));
  };

  const getRentableAssetNumbers = (itemType, isEdit = false, assetId) => {
    if (isEmpty(property) || isEmpty(itemType)) {
      return [];
    }

    let rentableItemsToExclude = currentRentableItemsForLease.map((item) => item.asset_id);

    if (isEdit) {
      // if isEdit, include the current asset in to list
      rentableItemsToExclude = currentRentableItemsForLease.map(
        (item) => item.asset_id !== assetId && item.asset_id
      );
    }

    // given item type, filter ancillary items for only that type and exclude already saved items
    // and map to label and value of asset number and id
    return property.ancillary_items
      .filter(
        (ancillaryItem) =>
          ancillaryItem.item_type === itemType && !rentableItemsToExclude.includes(ancillaryItem.id)
      )
      .map((ancillaryItem) => ({
        label: ancillaryItem.asset_number,
        value: ancillaryItem.id,
      }));
  };

  const getRentableAsset = (assetNumber) => {
    if (isEmpty(property) || !assetNumber) {
      return undefined;
    }

    return property.ancillary_items.find((ancillaryItem) => ancillaryItem.id === assetNumber);
  };

  const onRentalItemEdit = (item, index) => {
    setToggleNewItem(true);
    setIsEdit(true);
    setItemForEdit(item);
    setEditIndex(index);
  };

  const deleteRentableItemFromList = (index) => {
    let newValidRentableItems = [...currentRentableItemsForLease];
    newValidRentableItems.splice(index, 1);
    storeCurrentRentableItemsForLease(newValidRentableItems);
    if (!isEmpty(conditionalAddendums)) {
      storeConditionalAddendumsForLease(newValidRentableItems, conditionalAddendums);
    }
  };

  const resetAfterEdit = () => {
    setIsEdit(false);
    setItemForEdit({});
    setEditIndex(undefined);
  };

  return (
    <React.Fragment>
      {!isEmpty(currentRentableItemsForLease) &&
        currentRentableItemsForLease.map((item, index) => {
          if (isEdit && index === editIndex) return null;
          const asset = getRentableAsset(item.asset_id);
          return (
            <StyledSubcontentContainer>
              <Subcontent
                label={titleize(item.asset_type)}
                onEditClick={() => onRentalItemEdit(item, index)}
                onDeleteClick={() => deleteRentableItemFromList(index)}
                hideEdit={toggleNewItem || isEdit} // hide edit icon if currently adding or editting
              >
                <RentableItem asset={asset} selectedValues={item} />
              </Subcontent>
            </StyledSubcontentContainer>
          );
        })}
      {toggleNewItem ? (
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leasePricingPage.rentableItem'
            )}
            hideEdit
            hideDelete
          >
            <RentableItemForm
              setToggleNewItem={setToggleNewItem}
              getRentableAssets={getRentableAssets}
              getRentableAssetNumbers={getRentableAssetNumbers}
              property={property}
              validRentableItems={currentRentableItemsForLease}
              setValidRentableItemsInStore={setValidRentableItemsInStore}
              selectedLeaseStartDate={selectedLeaseStartDate}
              selectedEarlyMoveInDate={selectedEarlyMoveInDate}
              selectedLeaseEndDate={selectedLeaseEndDate}
              itemForEdit={itemForEdit}
              isEdit={isEdit}
              isMobile={isMobile}
              editIndex={editIndex}
              resetAfterEdit={resetAfterEdit}
            />
          </Subcontent>
        </StyledSubcontentContainer>
      ) : (
        <StyledButton
          label={getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.addItem')}
          onClick={() => {
            setToggleNewItem(true);
          }}
          size={isMobile ? 'large' : 'default'}
        />
      )}
    </React.Fragment>
  );
};

const mapStoreToProps = (store) => ({
  currentRentableItemsForLease: store.landlord.leaseCreatorV2.currentRentableItemsForLease,
  conditionalAddendums: store.landlord.leaseCreatorV2.conditionalAddendums,
});

const mapDispatchToProps = {
  storeCurrentRentableItemsForLease,
  storeConditionalAddendumsForLease,
};

export default connect(mapStoreToProps, mapDispatchToProps)(RentableItemList);
